import { useState, useEffect, useRef, useCallback } from "react";
import { FaLine } from "react-icons/fa";
import { onAuthStateChanged } from "firebase/auth";
import { auth as firebaseAuth } from "../firebase";
import { incrementLineApply } from "../utils/incrementLineApply";

// Chart.js
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Title,
} from "chart.js";

// 登録（未登録だと動きません）
Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Title);

// 影プラグイン（少しだけ立体感）
const shadowEffect = {
  id: "shadowEffect",
  beforeDatasetsDraw(chart) {
    const { ctx } = chart;
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.12)";
    ctx.shadowBlur = 8;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 4;
  },
  afterDatasetsDraw(chart) {
    chart.ctx.restore();
  },
};
Chart.register(shadowEffect);

// ---------- GA4 直送ユーティリティ ----------
const trackGA = (event, params = {}) => {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", event, params);
    }
  } catch (_) {}
};

// 地域ごとの想定時給
const areaData = {
  新宿: 6000, 六本木: 6500, 銀座: 7000, 新橋: 6000,
  上野: 5500, 池袋: 5500, 錦糸町: 5500, 渋谷: 6000, その他都内: 5000,
  大宮: 5000, その他埼玉: 4800, 横浜: 5200, 川崎: 5200, その他神奈川: 5000,
  船橋: 4900, 西船橋: 5000, 千葉中央駅: 5000, その他千葉: 4700,
  茨城: 4500, 栃木: 4500, 群馬: 4500,
};

const yen = new Intl.NumberFormat("ja-JP");

const prefersNoMotion =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Simulator = () => {
  const [area, setArea] = useState("新宿");
  const [days, setDays] = useState(16);
  const [pendingDays, setPendingDays] = useState(16);
  const [displayedSalary, setDisplayedSalary] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [user, setUser] = useState(null);
  const [applying, setApplying] = useState(false);

  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const animationRef = useRef(null);

  // 認証状態
  useEffect(() => {
    const unsub = onAuthStateChanged(firebaseAuth, (u) => setUser(u));
    return () => unsub();
  }, []);

  const hourly = areaData[area] || 5000;
  const mySiteSalary = hourly * 6 * days;
  const otherSiteSalary = Math.floor(mySiteSalary * 0.78);
  const salaryDifference = mySiteSalary - otherSiteSalary;

  const calculateSalary = useCallback((a, d) => {
    return (areaData[a] || 5000) * 6 * d;
  }, []);

  // 数字カウントアップ
  const animateSalary = useCallback(
    (finalSalary) => {
      if (prefersNoMotion) {
        setDisplayedSalary(finalSalary);
        setIsCalculating(false);
        return;
      }
      cancelAnimationFrame(animationRef.current);
      setIsCalculating(true);
      let current = 0;
      const duration = 800;
      const step = finalSalary / (duration / (1000 / 60));

      const tick = () => {
        current += step;
        if (current >= finalSalary) {
          setDisplayedSalary(finalSalary);
          setIsCalculating(false);
        } else {
          setDisplayedSalary(Math.floor(current));
          animationRef.current = requestAnimationFrame(tick);
        }
      };
      animationRef.current = requestAnimationFrame(tick);
    },
    [setDisplayedSalary]
  );

  // area/days 変更時に再計算
  useEffect(() => {
    animateSalary(calculateSalary(area, days));
    return () => cancelAnimationFrame(animationRef.current);
  }, [area, days, animateSalary, calculateSalary]);

  // グラフ描画（値が変わるたび更新 & 破棄）
  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    chartRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: ["New‑Nightlife 招待", "一般求人サイト"],
        datasets: [
          {
            label: "予想月収（円）",
            data: [mySiteSalary, otherSiteSalary],
            borderRadius: 8,
            backgroundColor: ["#ff6fa9", "#bdbdbd"],
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${yen.format(ctx.parsed.y)} 円`,
            },
          },
          legend: { display: false },
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            ticks: { callback: (v) => yen.format(v) },
            beginAtZero: true,
            grid: { color: "rgba(0,0,0,0.06)" },
          },
        },
        animation: prefersNoMotion ? false : undefined,
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [mySiteSalary, otherSiteSalary]);

  const handleSliderChange = (e) => setPendingDays(parseInt(e.target.value, 10));
  const commitDaysChange = () => setDays(pendingDays);

  // ★ LINEボタン：GA4直送 + 応募カウント
  const handleLineApply = async (e) => {
    // target="_blank" なので preventDefault は不要（発火→新規タブ）
    // 先にイベント送信（分析用に文脈も添付）
    trackGA("line_apply_click", {
      button_id: "simulator-line-button",
      link_url: "/go/line",
      area,
      days,
      hourly,
      mysite_salary: mySiteSalary,
      other_salary: otherSiteSalary,
      diff: salaryDifference,
      user_signed_in: user ? "1" : "0",
      event_category: "engagement",
      event_label: "simulator_cta",
    });

    if (applying) return;
    setApplying(true);

    if (!user) {
      setApplying(false);
      return;
    }
    try {
      await incrementLineApply(user);
    } catch (err) {
      console.error("応募カウントの更新に失敗:", err);
    }
    setApplying(false);
  };

  return (
    <div style={{ padding: 0, margin: 0 }}>
      <h1
        style={{
          fontSize: "20px",
          textAlign: "center",
          padding: "24px 12px 12px",
          lineHeight: 1.4,
          color: "#d81b60",
          background: "linear-gradient(90deg, #fff0f5, #ffe0ec, #fff0f5)",
          borderRadius: "12px",
          fontWeight: "bold",
        }}
      >
        未経験のための関東エリアのキャバクラ特集 | New‑Nightlife
      </h1>

      <div
        style={{
          maxWidth: "420px",
          margin: "48px auto 0",
          padding: "16px",
          background: "#fff5f9",
          borderRadius: "24px",
          border: "8px solid #ffb0d4",
          boxShadow: "0 10px 28px rgba(0,0,0,0.12)",
          textAlign: "center",
          fontFamily:
            "'M PLUS Rounded 1c','Kosugi Maru',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif",
        }}
      >
        <h2 style={{ fontSize: "20px", fontWeight: "bold", color: "#e91e63", lineHeight: 1.6 }}>
          出勤日数とエリアを選択して<br />
          予想月収をシミュレーション！
        </h2>

        <div
          style={{
            width: "60px",
            height: "4px",
            margin: "8px auto 20px",
            background: "linear-gradient(to right, #ff4fa2, #ff8ac7)",
            borderRadius: "2px",
          }}
        />

        <label
          htmlFor="area"
          style={{ display: "block", marginBottom: "12px", color: "#e91e63", fontWeight: "bold", fontSize: "16px" }}
        >
          勤務エリア
        </label>

        <select
          id="area"
          value={area}
          onChange={(e) => setArea(e.target.value)}
          style={{
            width: "100%",
            padding: "10px",
            fontSize: "14px",
            borderRadius: "8px",
            border: "2px solid #ffb0d4",
            marginBottom: "10px",
          }}
        >
          {Object.keys(areaData).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        <label
          htmlFor="days"
          style={{ display: "block", margin: "12px 0 12px", color: "#e91e63", fontWeight: "bold", fontSize: "16px" }}
        >
          出勤日数：<span style={{ color: "#333" }}>{pendingDays}日</span>
        </label>

        <input
          id="days"
          type="range"
          min="8"
          max="31"
          value={pendingDays}
          onChange={handleSliderChange}
          onPointerUp={commitDaysChange}
          onMouseUp={commitDaysChange}
          onTouchEnd={commitDaysChange}
          style={{ width: "100%", marginBottom: "20px" }}
          aria-valuemin={8}
          aria-valuemax={31}
          aria-valuenow={pendingDays}
        />

        <div style={{ marginBottom: "20px" }}>
          <p
            style={{
              fontSize: "26px",
              fontWeight: "bold",
              color: "#4caf50",
              textAlign: "center",
              margin: "0 0 16px",
              animation: isCalculating && !prefersNoMotion ? "pulse 1s infinite" : "none",
            }}
            aria-live="polite"
          >
            ¥{yen.format(displayedSalary)}
          </p>
          <p style={{ fontSize: "14px", color: "#888", marginTop: "30px" }}>一般求人サイトとの差額：</p>
          <p style={{ fontSize: "18px", fontWeight: "bold", color: "#e91e63", marginTop: "22px" }}>
            +{yen.format(salaryDifference)}円
          </p>
        </div>

        <div style={{ width: "100%", height: 220 }}>
          <canvas ref={canvasRef} style={{ maxWidth: "100%" }} aria-label="月収比較グラフ" role="img" />
        </div>

        <div style={{ marginTop: "28px" }}>
          <a
            id="simulator-line-button"
            href="/go/line"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLineApply}
            style={{ textDecoration: "none", display: "inline-block" }}
            aria-label="LINEで相談する（シミュレーターCTA）"
          >
            <button
              disabled={applying}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                background: "linear-gradient(to right, #00c300, #00e600)",
                color: "white",
                fontWeight: "bold",
                padding: "16px 28px",
                borderRadius: "999px",
                fontSize: "13px",
                textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                boxShadow: "0 6px 14px rgba(0,0,0,0.25)",
                letterSpacing: "0.5px",
                justifyContent: "center",
                border: "none",
                cursor: applying ? "not-allowed" : "pointer",
                opacity: applying ? 0.7 : 1,
              }}
            >
              <FaLine style={{ fontSize: "32px" }} />
              LINEで相談する
            </button>
          </a>
        </div>
      </div>

      {/* アニメ用（reduce motion考慮のため外出し） */}
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.03); }
          100% { transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
};

export default Simulator;
